export type SetupStep = 1 | 2 | 3

export type OnboardingPhase = 'setup' | 'explore'

export type ExploreStepId = 'monitors' | 'settings' | 'statusFinish'

export const ONBOARDING_SETUP_STEP_COUNT = 3
export const ONBOARDING_EXPLORE_STEP_COUNT = 3
export const ONBOARDING_TOTAL_STEPS =
  ONBOARDING_SETUP_STEP_COUNT + ONBOARDING_EXPLORE_STEP_COUNT

export const ONBOARDING_SETUP_PATH: Record<SetupStep, string> = {
  1: '/settings',
  2: '/escalation-policies/new',
  3: '/monitors/new',
}

export const ONBOARDING_SETUP_TARGET: Record<SetupStep, string> = {
  1: 'slack-connect',
  2: 'policy-channel',
  3: 'monitor-url',
}

export const ONBOARDING_SETUP_SUBMIT_TARGET: Record<2 | 3, string> = {
  2: 'policy-submit',
  3: 'monitor-submit',
}

export const ONBOARDING_SETUP_COPY: Record<
  SetupStep,
  { title: string; body: string }
> = {
  1: {
    title: 'Connect Slack',
    body: 'Connect to Slack so that when a monitor goes down you will receive a notification.',
  },
  2: {
    title: 'Create an escalation policy',
    body: 'Select which Slack channel notifications will be sent to.',
  },
  3: {
    title: 'Create your first monitor',
    body: 'Enter the URL of the site to monitor, then press Enter when ready.',
  },
}

export type ExploreStepConfig = {
  id: ExploreStepId
  path: string
  targetId: string
  targetIds?: string[]
  coachAnchorId?: string
  /** When set, coach panel is positioned using the union of these targets (step 4). */
  coachAnchorIds?: string[]
  title: string
  body: string
  finish?: boolean
}

export const EXPLORE_STEPS: ExploreStepConfig[] = [
  {
    id: 'monitors',
    path: '/monitors',
    targetId: 'nav-monitors-menu',
    targetIds: ['nav-monitors-menu', 'nav-monitors-menu-rail'],
    coachAnchorId: 'nav-monitors-menu-rail',
    coachAnchorIds: ['nav-monitors-menu-rail', 'nav-monitors-menu'],
    title: 'Monitor section',
    body: 'Here is the Monitor section — your checks and escalation policies live here.',
  },
  {
    id: 'settings',
    path: '/settings',
    targetId: 'nav-settings-menu-rail',
    targetIds: ['nav-settings-menu-rail'],
    coachAnchorId: 'nav-settings-menu-rail',
    title: 'Settings',
    body: 'Here is Settings — account, plan, and Slack integration.',
  },
  {
    id: 'statusFinish',
    path: '/status',
    targetId: 'nav-status-menu-rail',
    targetIds: ['nav-status-menu-rail'],
    coachAnchorId: 'nav-status-menu-rail',
    title: 'Status page',
    body: 'Here is Status — your monitors and uptime at a glance.',
    finish: true,
  },
]

export function exploreOverlayTargetIds(step: ExploreStepConfig | undefined): string[] {
  if (!step) return ['nav-status-menu-rail']
  return step.targetIds ?? [step.targetId]
}

export function exploreCoachAnchorId(step: ExploreStepConfig | undefined): string {
  if (!step) return 'nav-status-menu-rail'
  return step.coachAnchorId ?? step.targetId
}

export function exploreCoachAnchorIds(step: ExploreStepConfig | undefined): string[] {
  if (!step) return ['nav-status-menu-rail']
  if (step.coachAnchorIds?.length) return step.coachAnchorIds
  return [exploreCoachAnchorId(step)]
}

export const EXPLORE_ALLOWED_PATHS = [
  '/status',
  '/monitors',
  '/escalation-policies',
  '/settings',
] as const

export function globalOnboardingStepNumber(
  phase: OnboardingPhase,
  setupStep: SetupStep | null,
  exploreIndex: number,
): number {
  if (phase === 'setup' && setupStep) return setupStep
  return ONBOARDING_SETUP_STEP_COUNT + exploreIndex + 1
}

export function globalOnboardingStepLabel(
  phase: OnboardingPhase,
  setupStep: SetupStep | null,
  exploreIndex: number,
): string {
  const n = globalOnboardingStepNumber(phase, setupStep, exploreIndex)
  return `Step ${n} of ${ONBOARDING_TOTAL_STEPS}`
}

export function deriveSetupStep(options: {
  slackConnected: boolean
  policyCount: number
  monitorCount: number
}): SetupStep | null {
  if (options.monitorCount > 0) return null
  if (!options.slackConnected) return 1
  if (options.policyCount === 0) return 2
  return 3
}

export function expectedOnboardingPath(
  phase: OnboardingPhase | null,
  setupStep: SetupStep | null,
  exploreIndex: number,
): string | null {
  if (!phase) return null
  if (phase === 'setup' && setupStep) return ONBOARDING_SETUP_PATH[setupStep]
  if (phase === 'explore') {
    return EXPLORE_STEPS[exploreIndex]?.path ?? '/status'
  }
  return null
}

export function isAllowedSetupPath(step: SetupStep, pathname: string): boolean {
  return pathname === ONBOARDING_SETUP_PATH[step]
}

export function isAllowedExplorePath(pathname: string): boolean {
  return EXPLORE_ALLOWED_PATHS.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`),
  )
}

export function onboardingEntryPath(): string {
  return ONBOARDING_SETUP_PATH[1]
}
